import React, { useMemo } from 'react'
import { Tag, TreeSelect } from 'antd'
import { isEmpty } from 'lodash'

const { SHOW_CHILD } = TreeSelect

/**
 * ProjectGroup — รองรับ 2 โหมด:
 *  1. (ใหม่) รับ `data` เป็น flat array จาก GET_PROJECT_GROUP_V2
 *     พร้อม `selectedNameMap` ({ "id": "name" }) สำหรับ inactive items
 *  2. (เดิม) รับ `masterData` แบบ nested tree (ใช้ใน AdditionalBusiness)
 */
const ProjectGroup = (props) => {
  const {
    // props ใหม่
    data,
    selectedNameMap,
    // props เดิม (backward-compatible)
    masterData,
    // shared props
    setChoiceProjectCategory,
    setChoiceProjectGroup,
    choiceCompany,
    choiceProjectGroup,
    disabled,
  } = props

  let url_page = window.location.pathname

  const isInfoPage =
    !url_page.includes('/management/powerbi') &&
    !url_page.includes('/create-project') &&
    !url_page.includes('/information')

  // ─── โหมดใหม่: flat data จาก API ───────────────────────────────────────
  const flatTreeData = useMemo(() => {
    if (!data) return null

    const activeIds = new Set(data.map((item) => String(item.id)))

    // active items จาก API
    const activeOptions = data.map((item) => ({
      title: item.name_en || item.name,
      value: String(item.id),
      key: String(item.id),
      disabled: false,
    }))

    // inactive items — แสดงใน dropdown เฉพาะ view mode (disabled), edit mode ซ่อนออก
    const inactiveOptions = isInfoPage
      ? (choiceProjectGroup || [])
          .filter((id) => !activeIds.has(String(id)))
          .map((id) => ({
            title: selectedNameMap?.[String(id)] || String(id),
            value: String(id),
            key: String(id),
            disabled: true,
          }))
      : []

    return [...activeOptions, ...inactiveOptions]
  }, [data, choiceProjectGroup, selectedNameMap, isInfoPage])

  // ─── โหมดเดิม: nested tree (backward-compatible) ──────────────────────
  const legacyTreeData = useMemo(() => {
    if (!masterData) return []
    // filter ตาม choiceCompany แล้ว flatten children ออกมา (ไม่แสดง parent)
    const filtered = masterData.filter((item) =>
      choiceCompany?.includes(item?.value),
    )
    const children = []
    filtered.forEach((parent, idxParent) => {
      parent.children?.forEach((child, idxChild) => {
        children.push({
          ...child,
          key: `0-${idxParent}-${idxChild}`,
          disabled: false,
          children: null,
        })
      })
    })
    // เพิ่ม inactive items
    const activeIds = new Set(children.map((c) => c.value))
    const inactiveItems = (choiceProjectGroup || [])
      .filter((id) => !activeIds.has(String(id)))
      .map((id) => ({
        title: selectedNameMap?.[String(id)] || String(id),
        value: String(id),
        key: `inactive-${id}`,
        disabled: true,
      }))
    return [...children, ...inactiveItems]
  }, [masterData, choiceCompany, choiceProjectGroup, selectedNameMap])

  const treeData = flatTreeData !== null ? flatTreeData : legacyTreeData

  function onChange(value) {
    setChoiceProjectGroup(value)
    if (value.length === 0) {
      setChoiceProjectCategory([])
    }
  }

  const tProps = {
    treeData,
    value: choiceProjectGroup,
    onChange,
    treeCheckable: true,
    showCheckedStrategy: SHOW_CHILD,
    placeholder: 'Select Strategic Perspective',
    style: { width: '100%' },
  }

  if (isInfoPage) {
    return (
      <TreeSelect
        disabled={isEmpty(choiceCompany)}
        size="large"
        treeDefaultExpandAll={true}
        {...tProps}
        maxTagCount={0}
        maxTagPlaceholder={(event) => event[0]?.label}
        tagRender={(event) => {
          const { label } = event
          return (
            <Tag
              style={{
                display: 'flex',
                fontSize: 12,
                borderRadius: '16px',
                background: 'rgba(7, 78, 159, 0.1)',
                height: '32px',
                alignItems: 'center',
                color: '#074E9F',
              }}
            >
              {choiceProjectGroup?.length > 1
                ? 'Multi select strategic perspective'
                : label}
            </Tag>
          )
        }}
      />
    )
  }

  // หน้า edit — inactive items ไม่อยู่ใน dropdown แต่แสดงเป็น tag ปกติผ่าน labelInValue
  const labeledValue = (choiceProjectGroup || []).map((id) => ({
    label:
      treeData.find((item) => item.value === id)?.title ??
      (selectedNameMap?.[id] || id),
    value: id,
  }))

  return (
    <TreeSelect
      size="large"
      treeDefaultExpandAll={true}
      {...tProps}
      labelInValue={true}
      value={labeledValue}
      onChange={(values) => {
        const ids = values.map((v) => v.value)
        setChoiceProjectGroup(ids)
        if (ids.length === 0) setChoiceProjectCategory([])
      }}
      disabled={disabled}
    />
  )
}

export default ProjectGroup
